import React from 'react';

import classnames from 'classnames';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import { bindAll, filter, map, kebabCase, get } from 'lodash';

// Dependencies
import PropTypes from 'prop-types';

// React components
import RcSlider from 'rc-slider';

import { Tooltip, Text } from '@nerdwallet/currency';
import { Num, Tracking } from '@nerdwallet/nw-client-lib';

// Lib
import getTranslateCSS from '../../../../lib/getTranslateCSS';

// CSS
import './loan-term-slider.less';

const { formInputChanged } = Tracking;
const FORM_NAME = 'loan-term-slider';
const TERM_MIN = 1;
const LABEL_WIDTH = 88;

class LoanTermSlider extends React.Component {
  constructor(props) {
    super(props);

    bindAll(this, ['handleChange', 'onDragStart', 'onDragEnd', 'setLayout']);

    this.state = {
      dragging: false,
    };
  }

  componentDidMount() {
    this.setLayout();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.setLayout);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setLayout(null, nextProps.data.inputs.howLongStay);
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.setLayout);
    }
  }

  onDragStart() {
    this.setState({
      dragging: true,
    });
  }

  onDragEnd(value) {
    formInputChanged({
      formName: FORM_NAME,
      fieldName: 'stayyears_slider_slider',
      fieldValue: value,
    });

    // Set the new state on drag end.
    this.setState({
      dragging: false,
    });
  }

  onBarClick(value) {
    return () => {
      this.handleChange(value);

      formInputChanged({
        formName: FORM_NAME,
        fieldName: 'stayyears_slider_barchart_bar',
        fieldValue: value,
      });
    };
  }

  getYearsStaying() {
    return this.props.data.inputs.howLongStay;
  }

  getTermMax() {
    const { refinanceLoanTerm, originationYear, currentLoanTerm } =
      this.props.data.inputs;
    const currYear = new Date().getFullYear();

    return (
      Math.max(
        originationYear + currentLoanTerm,
        currYear + refinanceLoanTerm
      ) - currYear
    );
  }

  setLayout(event, value) {
    this.calculateWidth();
    this.setMessagesPosition(value || this.getYearsStaying());
  }

  setMessagesPosition(value) {
    const { messageBottomWidth } = this;

    this.setState({
      messageBottomPosition: this.calculateSliderMessagePosition(
        value,
        messageBottomWidth
      ),
    });
  }

  calculateWidth() {
    if (typeof window !== 'undefined' && this.domNode !== undefined) {
      this.width = this.domNode.getBoundingClientRect().width;
      this.sliderMargin = (this.sliderMarginPercent * this.width) / 100;
      this.messageTopWidth =
        this.sliderMessageTop?.getBoundingClientRect().width;
      this.messageBottomWidth =
        this.sliderMessageBottom?.getBoundingClientRect().width;
    }
  }

  calculateSliderMessagePosition(value, messageWidth) {
    const messagePosition =
      (this.width / this.TERM_MAX) * (value - 0.5) - messageWidth / 2;
    return Num.constrain(messagePosition, 0, this.width - (messageWidth + 18));
  }

  handleChange(value) {
    if (value !== this.getYearsStaying()) {
      this.props.onChange(
        {
          key: 'inputs.howLongStay',
          inputId: 'howLongStay',
        },
        value
      );
    }
  }

  renderSlider() {
    const sliderStyle = {
      width: `${this.sliderWidthPercent}%`,
      marginLeft: `${this.sliderMarginPercent}%`,
    };

    return (
      <div style={sliderStyle}>
        <RcSlider
          defaultValue={this.getYearsStaying()}
          value={this.getYearsStaying()}
          min={TERM_MIN}
          max={this.TERM_MAX}
          onBeforeChange={this.onDragStart}
          onAfterChange={this.onDragEnd}
          onChange={this.handleChange}
          tipFormatter={null}
        />
      </div>
    );
  }

  renderDataViz(data) {
    const OFFSET_TOP = 40;
    const OFFSET_BOTTOM = 80;
    const MAX_HEIGHT = 60;

    const howLongStay = this.getYearsStaying();
    const {
      breakevenBegin,
      breakevenEnd,
      details: {
        savings: { monthlyPayment },
      },
    } = data.outputs;
    const dataviz = get(data, 'outputs.dataviz', []);

    // Reference => 100% height
    let maxValue;
    dataviz.forEach((saving) => {
      if (!maxValue || Math.abs(saving) > maxValue) {
        maxValue = Math.abs(saving);
      }
    });

    const bars = map(data.outputs.dataviz, (saving, index) => {
      // Bar height in px compared to the biggest bar
      const currYear = index + 1;
      const barHeightPx = Math.max(5, Math.abs(saving / maxValue) * MAX_HEIGHT);

      const barStyle = {
        height: `${barHeightPx}px`,
      };

      if (saving >= 0) {
        barStyle.marginBottom = `${barHeightPx + OFFSET_TOP}px`;
      } else {
        barStyle.marginTop = `${barHeightPx + OFFSET_BOTTOM}px`;
      }
      const barClasses = classnames('lt-slider-chart-bar', {
        green:
          currYear >= breakevenBegin &&
          (!breakevenEnd || currYear < breakevenEnd) &&
          !(monthlyPayment < 0 && saving > 0),
        yellow:
          currYear >= breakevenEnd ||
          (currYear >= breakevenBegin && monthlyPayment < 0 && saving > 0),
        selected: howLongStay === currYear,
      });

      return (
        <div
          key={index}
          className={barClasses}
          style={barStyle}
          onClick={this.onBarClick(currYear)}
        />
      );
    });

    return (
      <div key="lt-slider-chart" className="lt-slider-chart">
        {bars}
      </div>
    );
  }

  renderMessageTop(data) {
    const messageBottomPositionStyle = getTranslateCSS(
      this.state.messageBottomPosition
    );

    let prevValue = TERM_MIN;
    const termMax = this.TERM_MAX;

    const howLongStay = this.getYearsStaying();
    const {
      breakevenBegin,
      breakevenEnd,
      dataviz,
      details: {
        savings: { totalSavings, monthlyPayment },
      },
    } = data.outputs;

    const sliderSections = map(this.splits, (splitConfig) => {
      const { label, value, analytics, help, title } = splitConfig;

      const savings = dataviz[value - 2];
      const sectionClasses = classnames(
        'lt-slider-section',
        [kebabCase(label)],
        {
          green:
            value > breakevenBegin &&
            (!breakevenEnd || value <= breakevenEnd) &&
            !(monthlyPayment < 0 && savings > 0),
          yellow: value > breakevenEnd || (monthlyPayment < 0 && savings > 0),
        }
      );

      const topLabelClasses = classnames('lt-slider-section-label', 'top', {
        hidden: value === 0 || label === '',
      });

      const sectionStyle = {
        width: `${((value - prevValue) / (termMax - TERM_MIN + 1)) * 100}%`,
      };

      const rtPosition = ((value - 1) / (termMax - TERM_MIN + 1)) * this.width;
      const topLabelStyle = {
        left: `${Num.constrain(
          rtPosition,
          LABEL_WIDTH / 2,
          this.width - LABEL_WIDTH / 2
        )}px`,
        width: 275,
        textAlign: 'left',
      };

      // 1 -> 0%, termMax => 100%
      // -0.5 because bars of the bar charts are centered with ticks of the slider
      const bottomLabelStyle = {
        left: `${((prevValue - 0.5) / (termMax - TERM_MIN + 1)) * 100}%`,
      };

      let topLabel;
      let separator;

      if (label) {
        topLabel = (
          <div
            key={`${label}-lt-slider-top-label`}
            className={topLabelClasses}
            style={topLabelStyle}
          >
            <Tooltip
              label={label}
              content={help}
              title={title}
              position="right"
              analytics={analytics}
            />
          </div>
        );

        separator = (
          <div
            key={`${label}-lt-slider-section-separator`}
            className="lt-slider-section-separator"
          />
        );
      }

      const sectionComponents = [
        topLabel,
        <div
          key={`${label}-lt-slider-key`}
          className={sectionClasses}
          style={sectionStyle}
        />,
        <div
          key={`${label}-lt-slider-label`}
          className="lt-slider-section-label bottom"
          style={bottomLabelStyle}
        >
          <Text size="small">{prevValue}</Text>
        </div>,
        separator,
      ];

      // Update the previous value
      prevValue = value;

      return sectionComponents;
    });

    const yearString = howLongStay === 1 ? 'year' : 'years';

    const savingsClasses = classnames('lt-slider-message-savings', {
      green:
        howLongStay >= breakevenBegin &&
        (!breakevenEnd || howLongStay < breakevenEnd) &&
        !(monthlyPayment < 0 && totalSavings > 0),
      yellow:
        howLongStay >= breakevenEnd || (monthlyPayment < 0 && totalSavings > 0),
    });

    const tooltipProps = {
      position: 'right',
      label: <Text size="small">Total Savings</Text>,
      title: 'More information about Total Savings',
      content:
        'How much less you would pay over this number of years by refinancing.',
      analytics: {
        entityName: 'help_button_interacted',
        eventType: 'help_bubble_interacted',
        payload: {
          additional_attributes: {
            help_button_location: 'refi_total_savings_slider',
          },
        },
      },
    };

    return (
      <React.Fragment>
        <div
          ref={(el) => {
            this.sliderMessageTop = el;
          }}
          className="lt-slider-message"
        >
          <div className="lt-slider-message-label">
            <Tooltip {...tooltipProps} />
          </div>
          <div className={savingsClasses}>
            {Num.formatCurrency(totalSavings, false)}
          </div>
        </div>
        <div className="lt-slider-section-wrapper">{sliderSections}</div>
        <div
          className="lt-slider-value"
          style={messageBottomPositionStyle}
          ref={(el) => {
            this.sliderMessageBottom = el;
          }}
        >
          <Text size="small" bold>{`${howLongStay} ${yearString}`}</Text>
          <div className="lt-slider-value-background" />
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { data } = this.props;
    const { breakevenBegin, breakevenEnd } = data.outputs;

    // We start indexing at 1
    this.TERM_MAX = this.getTermMax();

    this.sliderWidthPercent = ((this.TERM_MAX - 1) / this.TERM_MAX) * 100;
    this.sliderMarginPercent = (100 - this.sliderWidthPercent) / 2;

    this.splits = filter(
      [
        {
          value: breakevenBegin,
          label: (
            <Text size="x-small" light>
              Breakeven
            </Text>
          ),
          title: 'More information about Breakeven',
          help: 'The point at which interest savings will exceed closing costs.',
          analytics: {
            entityName: 'help_button_interacted',
            eventType: 'help_bubble_interacted',
            payload: {
              additional_attributes: {
                help_button_location: 'refi_breakeven_slider',
              },
            },
          },
        },
        {
          value: breakevenEnd,
        },
        {
          value: this.TERM_MAX + 1,
        },
      ],
      (config) => config.value !== undefined
    );

    const classes = classnames(this.props.className, 'lt-slider', {
      dragging: this.state.dragging,
    });

    return (
      <div
        ref={(el) => {
          this.domNode = el;
        }}
        className={classes}
        style={{ position: 'relative' }}
      >
        {this.renderDataViz(data)}
        {this.renderMessageTop(data)}
        {this.renderSlider()}
      </div>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
LoanTermSlider.displayName = 'LoanTermSlider';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
LoanTermSlider.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
LoanTermSlider.defaultProps = {
  onChange: () => {},
  className: null,
};

export default LoanTermSlider;
